import { useEffect, useState } from 'react';
import { getApiBaseUrl } from '../constants/environment';
import { CARD_TYPES, DOMAINS, KEYWORDS, RARITIES, REGIONS, SETS, SUPERTYPES, setReleaseDate } from '../constants';

interface Facets {
  cardTypes: string[];
  supertypes: string[];
  regions: string[];
  champions: string[];
  keywords: string[];
  rarities: string[];
  domains: string[];
  artists: string[];
  sets: string[];
  /** Creature subtypes / card-face identity tags (Dragon, Yordle, Pirate, etc.). */
  subtypes: string[];
  /** Uppercase set code → official name from Riftcodex (attached by `/api/facets`). */
  setNames: Record<string, string>;
}

const FALLBACK_SET_NAMES = Object.fromEntries(
  SETS.map((s) => [s.code.toUpperCase(), s.name])
) as Record<string, string>;

/** Sort set codes newest-first by release date. Sets with no release date go to the end. */
function sortSetsNewestFirst(codes: string[]): string[] {
  return [...codes].sort((a, b) => {
    const da = setReleaseDate(a);
    const db = setReleaseDate(b);
    if (da && db && da !== db) return db.localeCompare(da); // newer dates first
    if (da && !db) return -1; // dated sets before undated promos
    if (!db && da) return 1;
    // Both undated or same date — preserve SETS constant order
    const SETS_ORDER = new Map(SETS.map((s, i) => [s.code.toUpperCase(), i]));
    const ia = SETS_ORDER.get(a.toUpperCase()) ?? Number.MAX_SAFE_INTEGER;
    const ib = SETS_ORDER.get(b.toUpperCase()) ?? Number.MAX_SAFE_INTEGER;
    return ia - ib;
  });
}

const FALLBACK: Facets = {
  cardTypes: [...CARD_TYPES],
  supertypes: [...SUPERTYPES],
  regions: [...REGIONS],
  champions: [],
  keywords: [...KEYWORDS],
  rarities: [...RARITIES],
  domains: [...DOMAINS],
  artists: [],
  sets: sortSetsNewestFirst(SETS.map((s) => s.code)),
  subtypes: [],
  setNames: FALLBACK_SET_NAMES,
};

// ── Module-level shared cache ────────────────────────────────────────────────
// Multiple surfaces (search sidebar, set pages, filter menus) mount useFacets
// concurrently; without a shared cache each mount fired its own /api/facets
// request. One in-flight promise is shared and the resolved value is reused
// for the rest of the session (facets change only on catalog syncs).

let cachedFacets: Facets | null = null;
let inflight: Promise<Facets | null> | null = null;
const subscribers = new Set<(f: Facets) => void>();

function parseFacetsResponse(data: Record<string, unknown> | null): Facets | null {
  if (!data) return null;
  const d = data as Partial<Record<keyof Facets, string[]>> & {
    setNames?: Record<string, string>;
  };
  const rawNames = d.setNames && typeof d.setNames === 'object' ? d.setNames : {};
  const apiNames = Object.fromEntries(
    Object.entries(rawNames).map(([k, v]) => [String(k).toUpperCase(), String(v)])
  ) as Record<string, string>;
  return {
    cardTypes: d.cardTypes?.length ? d.cardTypes : FALLBACK.cardTypes,
    supertypes: d.supertypes?.length ? d.supertypes : FALLBACK.supertypes,
    regions: d.regions?.length ? d.regions : FALLBACK.regions,
    champions: d.champions || [],
    keywords: d.keywords?.length ? d.keywords : FALLBACK.keywords,
    rarities: d.rarities?.length ? d.rarities : FALLBACK.rarities,
    domains: d.domains?.length ? d.domains : FALLBACK.domains,
    artists: d.artists?.length ? d.artists : FALLBACK.artists,
    sets: sortSetsNewestFirst(d.sets?.length ? d.sets : FALLBACK.sets),
    subtypes: d.subtypes?.length ? d.subtypes : FALLBACK.subtypes,
    // Curated channel names win over the long catalog titles stored on cards.
    setNames: {
      ...FALLBACK_SET_NAMES,
      ...apiNames,
      JDG: FALLBACK_SET_NAMES.JDG,
      OPP: FALLBACK_SET_NAMES.OPP,
      PR: FALLBACK_SET_NAMES.PR,
    },
  };
}

function fetchFacetsOnce(): Promise<Facets | null> {
  if (cachedFacets) return Promise.resolve(cachedFacets);
  if (inflight) return inflight;
  inflight = fetch(`${getApiBaseUrl()}/api/facets`)
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      const parsed = parseFacetsResponse(data);
      if (parsed) {
        cachedFacets = parsed;
        for (const notify of subscribers) notify(parsed);
      }
      return parsed;
    })
    .catch(() => null)
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Test-only: clear the module cache between tests. */
export function __resetFacetsCacheForTests(): void {
  cachedFacets = null;
  inflight = null;
  subscribers.clear();
}

export function useFacets(): Facets {
  const [facets, setFacets] = useState<Facets>(cachedFacets ?? FALLBACK);

  useEffect(() => {
    if (cachedFacets) {
      setFacets(cachedFacets);
      return;
    }
    let cancelled = false;
    const notify = (f: Facets) => {
      if (!cancelled) setFacets(f);
    };
    subscribers.add(notify);
    void fetchFacetsOnce();
    return () => {
      cancelled = true;
      subscribers.delete(notify);
    };
  }, []);

  return facets;
}
